import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { ArrowLeft, ArrowRight, EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";
import { forgotPasswordSchema } from "@reactive-resume/dto";
import {
  Alert,
  AlertDescription,
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import type { z } from "zod";
import { motion } from "framer-motion";

import { useForgotPassword } from "@/client/services/auth";

type FormValues = z.infer<typeof forgotPasswordSchema>;

const ease = [0.22, 1, 0.36, 1] as const;

const fadeUp = (i: number) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { delay: i * 0.06, duration: 0.4, ease },
});

export const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const { forgotPassword, loading } = useForgotPassword();

  const form = useForm<FormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: "" },
  });

  const onSubmit = async (data: FormValues) => {
    await forgotPassword(data);
    setSubmitted(true);
    form.reset();
  };

  /* ── État succès ── */
  if (submitted) {
    return (
      <>
        <Helmet>
          <title>{t`E-mail envoyé`} — Mon CV Pro</title>
        </Helmet>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease }}
          className="flex flex-col items-center gap-5 py-6 text-center"
        >
          <div className="bg-[#3ecf8e]/12 flex h-16 w-16 items-center justify-center rounded-full">
            <CheckCircle size={34} weight="fill" className="text-[#3ecf8e]" />
          </div>

          <div className="space-y-1.5">
            <h2 className="text-[1.5rem] font-extrabold tracking-[-0.02em] text-gray-900">
              {t`Vérifiez votre boîte mail`}
            </h2>

            <p className="text-[0.85rem] leading-relaxed text-gray-400">
              {t`Un lien de réinitialisation a été envoyé à votre adresse si un compte existe.`}
            </p>
          </div>

          <button
            onClick={() => void navigate("/client/auth/login")}
            className="mt-2 inline-flex items-center gap-1.5 text-[0.82rem] font-medium text-[#472cb3] hover:underline"
          >
            <ArrowLeft size={14} />
            {t`Retour à la connexion`}
          </button>
        </motion.div>
      </>
    );
  }

  /* ── Formulaire ── */
  return (
    <>
      <Helmet>
        <title>{t`Mot de passe oublié`} — Mon CV Pro</title>
      </Helmet>

      {/* Retour */}
      <motion.div {...fadeUp(0)} className="mb-5">
        <button
          onClick={() => void navigate(-1)}
          className="inline-flex items-center gap-1.5 text-[0.8rem] font-medium text-gray-400 transition-colors hover:text-gray-700"
        >
          <ArrowLeft size={14} />
          {t`Retour`}
        </button>
      </motion.div>

      {/* Eyebrow */}
      <motion.p
        {...fadeUp(1)}
        className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[0.2em] text-[#3ecf8e]"
      >
        MON CV PRO
      </motion.p>

      {/* Titre */}
      <motion.h1
        {...fadeUp(2)}
        className="mb-1 text-[1.85rem] font-extrabold leading-[1.18] tracking-[-0.025em] text-gray-900"
      >
        Mot de passe oublié ?
      </motion.h1>

      {/* Sous-titre */}
      <motion.p {...fadeUp(3)} className="mb-6 text-[0.84rem] leading-relaxed text-gray-400">
        Saisissez votre adresse e-mail et nous vous enverrons un lien pour réinitialiser votre mot
        de passe.
      </motion.p>

      <Form {...form}>
        <form className="flex flex-col gap-y-3.5" onSubmit={form.handleSubmit(onSubmit)}>
          <motion.div {...fadeUp(4)}>
            <FormField
              name="email"
              control={form.control}
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel className="text-[0.78rem] font-semibold text-gray-600">
                    {t`Adresse e-mail`}
                  </FormLabel>

                  <FormControl>
                    <div className="relative">
                      <EnvelopeSimple
                        size={16}
                        className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-[#667085]"
                      />

                      <Input
                        placeholder="exemple@domaine.com"
                        autoComplete="email"
                        className={cn(
                          "h-11 rounded-xl border border-[#C9D3E6]",
                          "bg-white pl-10 text-sm font-medium text-[#172033]",
                          "caret-[#6687F0] shadow-sm",
                          "placeholder:font-normal placeholder:text-[#98A2B3]",
                          "transition-[border-color,box-shadow,background-color]",
                          "hover:border-[#AEBDE0]",
                          "focus-visible:border-[#6687F0]",
                          "focus-visible:bg-white",
                          "focus-visible:ring-2 focus-visible:ring-[#6687F0]/20",
                          "",
                          " ",
                          "",
                          "",
                          "",
                        )}
                        {...field}
                      />
                    </div>
                  </FormControl>

                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
          </motion.div>

          <motion.div {...fadeUp(5)} className="pt-0.5">
            <motion.div whileHover={{ scale: 1.012 }} whileTap={{ scale: 0.988 }}>
              <Button
                type="submit"
                disabled={loading}
                className="group flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-[#472cb3] text-[0.92rem] font-semibold text-white shadow-[0_4px_16px_rgba(71,44,179,0.3)] transition-all duration-200 hover:bg-[#3a22a0]"
              >
                {t`Envoyer le lien`}

                <ArrowRight
                  size={16}
                  weight="bold"
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </Button>
            </motion.div>
          </motion.div>
        </form>
      </Form>
    </>
  );
};
